import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, CheckCircle2, AlertTriangle, Clock, 
  FileText, Search, ArrowRight, Zap, 
  Calendar, DollarSign, Activity, ChevronRight, User, Settings, ExternalLink, Plus, Play
} from 'lucide-react';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Dialog, DialogContent, DialogTrigger } from '../../ui/dialog';

const InsuranceVerificationSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Abstract Background - Blue/Slate Theme */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[80px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-slate-200/50 rounded-full blur-[80px] translate-x-1/2 translate-y-1/2" />

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-8 relative z-10">
            {/* Left Side: Context */}
            <div className="w-[42%] flex flex-col justify-center space-y-6">
                
                {/* Agent Header (Inline) */}
                <div className="flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg w-fit mb-8">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                         <img src="/assets/amanda.png" alt="Amanda" className="w-full h-full object-cover" />
                    </div>
                    <div className="text-left">
                        <h3 className="text-lg font-bold text-slate-900 leading-none">Amanda</h3>
                        <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Insurance Verification</p>
                    </div>
                </div>

                <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                    Amanda automatically retrieves benefits breakdowns and saves them in Asha PMS.
                </h2>
                
                <p className="text-lg text-slate-600 leading-relaxed">
                    Amanda automatically verifies benefits for every appointment on your schedule. 
                    See full procedure-level breakdowns and catch coverage issues days in advance.
                </p>

                <div className="space-y-4 pt-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <div className="p-2 bg-green-100 rounded-lg text-green-600 mt-1">
                            <CheckCircle2 className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg">Verified vs. Escalation</h4>
                            <p className="text-sm text-slate-500">Instantly see who is good to go and who needs a manual check.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600 mt-1">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg">Detailed Breakdowns</h4>
                            <p className="text-sm text-slate-500">Procedure-level coverage, deductibles, and remaining maximums.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Graphic */}
            <div className="w-[58%] h-full flex items-center relative">
                <motion.div 
                    initial={{ y: 40, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="w-full h-[650px] bg-slate-50 rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col relative"
                >
                    {/* Demo Button Overlay - Top Right */}
                    <div className="absolute bottom-6 right-6 z-50 flex items-center gap-4">
                        <Dialog>
                            <DialogTrigger asChild>
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="group relative inline-flex items-center gap-3 bg-slate-900/90 backdrop-blur-md px-5 py-3 rounded-full shadow-2xl border border-slate-700/50 hover:border-blue-500/50 transition-all duration-300"
                                >
                                    <div className="relative flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                                        <Play className="w-3.5 h-3.5 ml-0.5 fill-current" />
                                        <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20" />
                                    </div>
                                    <div className="text-left pr-1">
                                        <div className="text-xs font-bold text-white">Watch Live Demo</div>
                                        <div className="text-[10px] text-slate-300">2 min video</div>
                                    </div>
                                </motion.button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-none">
                                <div className="relative w-full pt-[62.5%]">
                                    <iframe 
                                        src="https://www.loom.com/embed/f615bd8955454dea968370c598d8d3ea" 
                                        frameBorder="0" 
                                        webkitallowfullscreen="true" 
                                        mozallowfullscreen="true" 
                                        allowFullScreen 
                                        className="absolute top-0 left-0 w-full h-full"
                                    ></iframe>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>

                    {/* Header Bar */}
                    <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                        <div>
                            <h1 className="text-xl font-bold text-slate-900">AI Insurance Verification</h1>
                            <div className="flex items-center gap-3 text-xs mt-1">
                                <div className="flex items-center gap-1 text-blue-600 font-medium">
                                    <Clock className="h-3 w-3" />
                                    <span>Next run: Tomorrow at 6:00 AM</span>
                                </div>
                                <span className="text-slate-300">•</span>
                                <span className="text-slate-500">Last: Today at 6:00 AM</span>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" className="gap-2">
                            <Settings className="h-4 w-4" />
                            Settings
                        </Button>
                    </div>

                    <div className="p-6 overflow-y-auto space-y-6 bg-slate-50/50 pb-20">
                        {/* Insights Card */}
                        <Card className="bg-white border-slate-200 shadow-sm">
                            <CardContent className="p-5">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <Zap className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-slate-900 font-medium">Amanda identified 7 procedures that could be denied.</p>
                                        </div>
                                    </div>
                                    <Button variant="outline" size="sm" className="text-slate-600">
                                        View Inbox <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </div>
                                
                                <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                                            AI
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-2">
                                                    <Shield className="h-3 w-3 text-slate-400" />
                                                    <span className="font-medium text-sm text-slate-900">Sarah Johnson</span>
                                                </div>
                                                <span className="text-xs text-slate-400">2h ago</span>
                                            </div>
                                            <p className="text-sm text-slate-600 leading-relaxed">
                                                Patient has only $150 remaining in annual benefits but is scheduled for a $950 crown (D2740) on May 10th. Estimated patient responsibility is high.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Table Section */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex gap-2">
                                    <Badge variant="secondary" className="bg-slate-200 text-slate-700 hover:bg-slate-200">All</Badge>
                                    <Badge variant="outline" className="text-slate-600">Escalation</Badge>
                                    <Badge variant="outline" className="text-slate-600">Verified</Badge>
                                </div>
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
                                    <Plus className="h-4 w-4" /> New Verification
                                </Button>
                            </div>

                            <Card className="overflow-hidden border-slate-200 shadow-sm bg-white">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="hover:bg-transparent border-slate-100">
                                            <TableHead className="w-[180px]">Patient</TableHead>
                                            <TableHead>Appointment</TableHead>
                                            <TableHead>Carrier</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Breakdown</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {mockPatients.map((patient, i) => (
                                            <TableRow key={i} className="hover:bg-slate-50 border-slate-100">
                                                <TableCell>
                                                    <div>
                                                        <div className="font-medium text-slate-900">{patient.name}</div>
                                                        <div className="text-xs text-slate-500">ID: {patient.id}</div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div>
                                                        <div className="text-sm text-slate-900">{patient.time}</div>
                                                        <div className="text-xs text-slate-500">{patient.type}</div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm text-slate-600">{patient.carrier}</span>
                                                </TableCell>
                                                <TableCell>
                                                    {patient.status === 'Verified' && (
                                                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 gap-1">
                                                            <CheckCircle2 className="w-3 h-3" /> Verified
                                                        </Badge>
                                                    )}
                                                    {patient.status === 'Escalation' && (
                                                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 gap-1">
                                                            <AlertTriangle className="w-3 h-3" /> Escalation
                                                        </Badge>
                                                    )}
                                                    {patient.status === 'Pending' && (
                                                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 gap-1">
                                                            <Clock className="w-3 h-3" /> Pending
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="sm" className="h-8 text-blue-600 hover:text-blue-700 p-0">
                                                        View <ExternalLink className="ml-1 w-3 h-3" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Card>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </div>
  );
};

const mockPatients = [
    {
        name: "Rachel Harris",
        id: "43534",
        time: "Tomorrow, 3:30 PM",
        type: "Limited Exam",
        carrier: "Delta Dental",
        status: "Verified"
    },
    {
        name: "Andrew Garfield",
        id: "43545",
        time: "Tomorrow, 4:30 PM",
        type: "Emergency Exam",
        carrier: "United Healthcare",
        status: "Escalation"
    },
    {
        name: "Johnny Smith",
        id: "57564",
        time: "May 6th, 10:30 AM",
        type: "Crown (D2740)",
        carrier: "Aetna",
        status: "Pending"
    },
    {
        name: "Sarah Johnson",
        id: "88921",
        time: "May 10th, 9:00 AM",
        type: "Root Canal",
        carrier: "MetLife",
        status: "Escalation"
    },
    {
        name: "Mike Ross",
        id: "12344",
        time: "May 12th, 2:00 PM",
        type: "Cleaning",
        carrier: "Cigna",
        status: "Verified"
    }
];

export default InsuranceVerificationSlide;

